import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, ArrowRight } from "lucide-react";
import { OPEN_ROLES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { full_name: "", email: "", phone: "", position: "", cover_letter: "" };

export default function Careers() {
  const [openRole, setOpenRole] = useState(null);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.position) {
      toast.error("Please fill in name, email and position.");
      return;
    }
    try {
      setLoading(true);
      await axios.post(`${API}/careers/apply`, form);
      toast.success("Application received. Our talent team will be in touch.");
      setForm(initial);
      setOpenRole(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-careers">
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16">
          <div className="overline rule-teal">Careers</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-6 leading-[1.02] text-[#0B2E5C] max-w-3xl">
            Build a career on better fundamentals.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#475569] leading-relaxed font-light">
            At Mizan you'll work on engagements that matter, with partners who
            mentor in person. We invest in ACCA / CPA candidates and high‑potential
            auditors who care about the craft.
          </p>
        </div>
      </section>

      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="careers-why">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
          {[
            { t: "Mentorship at the top", b: "Partners review files and coach in person — no anonymous email feedback." },
            { t: "Real engagement variety", b: "Banks, NGOs, telecoms, public sector — you'll never get stuck on one industry." },
            { t: "Qualification support", b: "Structured study leave, exam fees and dedicated tuition for ACCA / CPA." },
          ].map((x, i) => (
            <div key={i} data-testid={`why-${i}`} className="bg-white p-10">
              <div className="font-display text-3xl text-[#2BA8B8]">0{i + 1}</div>
              <h3 className="font-display text-2xl text-[#0B2E5C] mt-3 leading-snug">{x.t}</h3>
              <p className="mt-4 text-[#475569] font-light leading-relaxed">{x.b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white" data-testid="careers-roles">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="overline rule-teal">Open roles</div>
          <h2 className="font-display text-4xl sm:text-5xl mt-4 leading-tight text-[#0B2E5C]">
            We're hiring across the practice.
          </h2>

          <div className="mt-12 border border-[#E2E8F0]">
            {OPEN_ROLES.map((r, i) => (
              <div
                key={r.title}
                data-testid={`role-${i}`}
                className={`p-8 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-start ${
                  i !== 0 ? "border-t border-[#E2E8F0]" : ""
                } hover:bg-[#F8FAFC] transition-colors`}
              >
                <div className="md:col-span-5">
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-[#2BA8B8]" strokeWidth={1.5} />
                    <h3 className="font-display text-2xl text-[#0B2E5C] leading-tight">
                      {r.title}
                    </h3>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#475569]">
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" /> {r.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {r.type}
                    </span>
                  </div>
                </div>
                <p className="md:col-span-5 text-[#475569] font-light leading-relaxed text-sm">
                  {r.description}
                </p>
                <div className="md:col-span-2 md:text-right">
                  <Dialog
                    open={openRole === r.title}
                    onOpenChange={(o) => {
                      setOpenRole(o ? r.title : null);
                      if (o) setForm({ ...initial, position: r.title });
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid={`role-apply-${i}`}
                        className="rounded-sm border-[#0B2E5C] text-[#0B2E5C] hover:bg-[#0B2E5C] hover:text-white h-10 px-5 text-sm"
                      >
                        Apply <ArrowRight className="ml-2 h-3.5 w-3.5" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="rounded-sm max-w-lg" data-testid="role-apply-dialog">
                      <DialogHeader>
                        <DialogTitle className="font-display text-2xl text-[#0B2E5C]">
                          Apply: {r.title}
                        </DialogTitle>
                        <DialogDescription className="text-[#475569]">
                          Tell us about yourself. We'll respond within a week.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="career-form">
                        <div>
                          <Label htmlFor="full_name" className="text-xs uppercase tracking-wider text-[#475569]">
                            Full name
                          </Label>
                          <Input
                            id="full_name"
                            name="full_name"
                            required
                            value={form.full_name}
                            onChange={onChange}
                            className="mt-2 rounded-sm focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                            data-testid="career-input-name"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[#475569]">
                              Email
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              required
                              value={form.email}
                              onChange={onChange}
                              className="mt-2 rounded-sm focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                              data-testid="career-input-email"
                            />
                          </div>
                          <div>
                            <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-[#475569]">
                              Phone
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              value={form.phone}
                              onChange={onChange}
                              className="mt-2 rounded-sm focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                              data-testid="career-input-phone"
                            />
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="cover_letter" className="text-xs uppercase tracking-wider text-[#475569]">
                            Cover letter
                          </Label>
                          <Textarea
                            id="cover_letter"
                            name="cover_letter"
                            rows={5}
                            value={form.cover_letter}
                            onChange={onChange}
                            className="mt-2 rounded-sm focus-visible:ring-[#2BA8B8] focus-visible:border-[#2BA8B8]"
                            data-testid="career-input-cover"
                          />
                        </div>
                        <Button
                          type="submit"
                          disabled={loading}
                          data-testid="career-submit-button"
                          className="w-full rounded-sm bg-[#0B2E5C] hover:bg-[#082142] text-white h-11"
                        >
                          {loading ? "Submitting..." : "Submit application"}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
